import { useState, useEffect } from "react";
import { Eye, Loader2, Zap, FileText, Calendar, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

interface VaultDocument {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  smart_badges: string[];
  ai_summary: string | null;
  toxicity_score: number | null;
  created_at: string;
}

interface DocumentViewerModalProps {
  document: VaultDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNeutralize?: (doc: VaultDocument) => void;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentViewerModal = ({
  document: doc,
  open,
  onOpenChange,
  onNeutralize,
}: DocumentViewerModalProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !doc) {
      setSignedUrl(null);
      setTextContent(null);
      return;
    }

    const loadContent = async () => {
      setIsLoading(true);
      try {
        const isText = doc.file_type === "text/plain" || doc.file_type === "text/csv";

        if (isText) {
          const { data, error } = await supabase.storage
            .from("vault-files")
            .download(doc.file_path);
          if (error) throw error;
          const text = await data.text();
          setTextContent(text);
        } else {
          const { data, error } = await supabase.storage
            .from("vault-files")
            .createSignedUrl(doc.file_path, 120);
          if (error) throw error;
          setSignedUrl(data.signedUrl);
        }
      } catch (error) {
        console.error("Failed to load document:", error);
        toast.error("Failed to load document preview");
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [open, doc]);

  if (!doc) return null;

  const isImage = doc.file_type.startsWith("image/");
  const isPdf = doc.file_type === "application/pdf";
  const isText = doc.file_type === "text/plain" || doc.file_type === "text/csv";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col bg-charcoal border-glass-border p-0 gap-0">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Eye className="w-5 h-5 text-cyan" />
            <span className="truncate">{doc.file_name}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Metadata Bar */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-glass-border text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <HardDrive className="w-3.5 h-3.5" />
            {formatFileSize(doc.file_size)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(doc.created_at).toLocaleDateString()}
          </span>
          {doc.smart_badges && doc.smart_badges.length > 0 &&
            doc.smart_badges.map((badge, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {badge}
              </Badge>
            ))}
        </div>

        {/* AI Summary snippet */}
        {doc.ai_summary && (
          <div className="px-5 py-3 border-b border-glass-border">
            <p className="text-xs font-medium text-muted-foreground mb-1">AI Summary</p>
            <p className="text-sm leading-relaxed line-clamp-3">{doc.ai_summary}</p>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 min-h-0 p-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-cyan animate-spin" />
            </div>
          ) : isImage && signedUrl ? (
            <div className="flex items-center justify-center max-h-[50vh] overflow-auto rounded-lg bg-charcoal-medium">
              <img
                src={signedUrl}
                alt={doc.file_name}
                className="max-w-full max-h-[50vh] object-contain rounded-lg"
              />
            </div>
          ) : isPdf && signedUrl ? (
            <iframe
              src={signedUrl}
              title={doc.file_name}
              className="w-full h-[50vh] rounded-lg border border-glass-border bg-white"
            />
          ) : isText && textContent !== null ? (
            <ScrollArea className="h-[50vh] rounded-lg border border-glass-border bg-charcoal-medium">
              <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words text-foreground">
                {textContent}
              </pre>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="w-12 h-12 mb-3 opacity-50" />
              <p>Preview not available for this file type</p>
            </div>
          )}
        </div>

        {/* Footer with Neutralize */}
        {onNeutralize && (
          <div className="px-5 py-4 border-t border-glass-border">
            <button
              onClick={() => {
                onOpenChange(false);
                onNeutralize(doc);
              }}
              className={cn(
                "w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.01] transition-transform",
                "shadow-[0_0_20px_rgba(0,242,255,0.3)]"
              )}
            >
              <Zap className="w-5 h-5" />
              Neutralize This Document
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewerModal;
